<template>
  <v-form v-model="mainFormValid">
    <v-row>
      <v-col>
        <v-text-field
          v-model="internalValue.name"
          :label="$t('trainingsession.fields.session.name')"
          :rules="$options.rules.name"
          :dense="initialValuesDefined"
          @input="initialValuesDefined && change()"
        />
      </v-col>
      <v-col cols="auto">
        <ag-language-field
          v-model="internalValue.lang"
          :label="$t('general.language')"
          :dense="initialValuesDefined"
        />
      </v-col>
      <v-col cols="auto">
        <ag-number-select
          v-model="internalValue.nbPlayer"
          :values="nbPlayersAvailable"
          :label="$t('trainingsession.fields.session.nbPlayer')"
          :rules="$options.rules.nbPlayer"
          :dense="initialValuesDefined"
        />
      </v-col>
    </v-row>

    <template v-if="initialValuesDefined">
      <multiproject-session-form
        v-model="internalValue.multiProject"
        :multi-project-version-list="multiProjectVersionList"
        :multi-project-draw-list="multiProjectDrawList"
        :nb-project-availables="nbProjectAvailables"
        class="mb-2"
        @input="change"
      />

      <multitask-session-form
        v-model="internalValue.multiTask"
        class="mb-2"
        @input="change"
      />

      <networkdiagram-session-form
        v-model="internalValue.networkDiagram"
        class="mb-2"
        @input="change"
      />

      <ontime-session-form
        v-model="internalValue.onTime"
        :on-time-version-list="onTimeVersionList"
        class="mb-2"
        @input="change"
      />

      <criticalchain-session-form
        v-model="internalValue.criticalChain"
        @input="change"
      />
    </template>
  </v-form>
</template>

<script>
import _pickBy from 'lodash/pickBy'
import _mapValues from 'lodash/mapValues'

import FormRules from 'src/utils/form-rules'
import AgLanguageField from 'src/components/common/AgLanguageField'
import AgNumberSelect from 'src/components/common/AgNumberSelect'

import MultiprojectSessionForm from './MultiprojectSessionForm'
import MultitaskSessionForm from './MultitaskSessionForm'
import NetworkdiagramSessionForm from './NetworkdiagramSessionForm'
import OntimeSessionForm from './OntimeSessionForm'
import CriticalchainSessionForm from './CriticalchainSessionForm'

export default {
  name: 'SessionForm',
  components: {
    AgLanguageField,
    AgNumberSelect,
    MultiprojectSessionForm,
    MultitaskSessionForm,
    NetworkdiagramSessionForm,
    OntimeSessionForm,
    CriticalchainSessionForm,
  },
  props: {
    multiProjectVersionList: {
      type: Array,
      required: true,
    },
    multiProjectDrawList: {
      type: Array,
      required: true,
    },
    onTimeVersionList: {
      type: Array,
      required: true,
    },
    multiTaskSettings: {
      type: Object,
      required: true,
    },
    maxPlayersAvailable: {
      type: Number,
      default: 10,
    },
  },
  data: () => ({
    internalValue: {
      name: '',
      lang: '',
      nbPlayer: 0,
      multiProject: {
        active: false,
        versionId: '',
        nbProject: 0,
        standardDrawId: '',
        ccpmDrawId: '',
      },
      multiTask: {
        active: false,
        lengthOfChoiceList: null,
        mistakePenality: null,
      },
      networkDiagram: {
        active: false,
      },
      onTime: {
        active: false,
        versionId: null,
      },
      criticalChain: {
        active: false,
      },
    },
    mainFormValid: false,
  }),
  computed: {
    initialValuesDefined () {
      return !!this.internalValue.lang && !!this.internalValue.nbPlayer
    },
    hasGameInSession () {
      return this.internalValue?.multiProject?.active
        || this.internalValue?.multiTask?.active
        || this.internalValue?.networkDiagram?.active
        || this.internalValue?.onTime?.active
        || this.internalValue?.criticalChain?.active
    },
    isValidSession () {
      return this.mainFormValid && this.initialValuesDefined && this.hasGameInSession
    },
    nbPlayersAvailable () {
      return new Array(Math.min(this.maxPlayersAvailable, 10))
        .fill(null)
        .map((_, index) => index + 1)
    },
    nbProjectAvailables () {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
        .filter(nb => nb >= this.internalValue.nbPlayer)
    },
    defaultMultiprojectVersion () {
      return this.multiProjectVersionList
        .find(item => item.lang === this.internalValue.lang && item.defaultVersion)
    },
    defaultStandardDraw () {
      return this.multiProjectDrawList
        .find(item => item.type === 'standard' && item.defaultDraw)
    },
    defaultCcpmDraw () {
      const ccpmDrawList = this.multiProjectDrawList.filter(draw => draw.type === 'ccpm')
      const defaultCcpmDraw = ccpmDrawList.find(draw => draw.defaultDraw)
      return this.defaultStandardDraw?.relatedDraw
        ? ccpmDrawList.find(draw => draw.id === this.defaultStandardDraw.relatedDraw)
        : defaultCcpmDraw
    },
    clearInternalValue () {
      const {
        name, lang, nbPlayer, ...games
      } = this.internalValue

      const availableGames = _pickBy(games, game => game.active)
      const clearAvailableGames = _mapValues(availableGames, ({ active, ...game }) => game)

      return {
        name, lang, nbPlayer, ...clearAvailableGames,
      }
    },
  },
  watch: {
    isValidSession: {
      immediate: true,
      handler () {
        this.$emit('validate', this.isValidSession)
      },
    },
    'internalValue.lang': {
      handler () {
        // Set the values that depend on lang value
        this.internalValue.multiProject.versionId = this.defaultMultiprojectVersion?.id ?? null

        // Trigger event only if all initial values are defined
        if (this.initialValuesDefined) {
          this.change()
        }
      },
    },
  },
  created () {
    const { multiProject, multiTask } = this.internalValue
    // Initialize default values without dependencies
    multiProject.standardDrawId = this.defaultStandardDraw?.id ?? null
    multiProject.ccpmDrawId = this.defaultCcpmDraw?.id ?? null
    multiTask.lengthOfChoiceList = this.multiTaskSettings?.lengthOfChoiceList ?? null
    multiTask.mistakePenality = this.multiTaskSettings?.mistakePenality ?? null
  },
  methods: {
    change () {
      this.$emit('input', this.clearInternalValue)
      this.$emit('validate', this.isValidSession)
    },
  },
  rules: {
    name: [
      FormRules.required('error.form.required'),
      FormRules.minLength('error.form.minLength', { length: 5 }),
    ],
    nbPlayer: [
      FormRules.required('error.form.required'),
    ],
  },
}
</script>
